import React, { useEffect } from 'react';
import { Container } from '@mui/material';
import { observer } from 'mobx-react';

import { angularizeWithProviders } from '../../../shared/providers';

import { ChallengesHeader } from '../../components/ChallengesHeader/ChallengesHeader';
import { useStepper } from '../../../shared/hooks/useStepper';
import { TwoFAStep } from './containers/TwoFAStep/TwoFAStep';
import { BlackBgRow } from '../../../shared/components/ui-components/BlackBgRow/BlackBgRow';
import { SignUpInfoStep } from './containers/SignUpInfoStep/SignUpInfoStep';
import { getRouteParam, goTo } from '../../../shared/utils/angularUtils';
import { twoFAService } from '../../../auth-management';
import { RESPONSE_STATUSES } from '../../../shared/constants/RESPONSE_STATUSES';
import { challengeSignUpStore } from '../../stores/challengeSignUpStore';
import { pathQueryParams } from '../../constants/pathQueryParams';
import { UnavailableWhenLoggedIn } from '../../../shared/containers/UnavailableWhenLoggedIn/UnavailableWhenLoggedIn';
import { useRedirectOnReload } from '../../../shared/hooks/useRedirectOnReload';

const NgzChallengeSignUp = observer(() => {
  const { activeStep, goToNextStep } = useStepper();

  const {
    setParticipantId,
    setCustomer,
    getEntityData,
    setChallengeId,
    setDefaultValues,
  } = challengeSignUpStore;

  const challengeId = getRouteParam('challengeId');

  useRedirectOnReload(`/challenge-landing/${challengeId}`);

  useEffect(() => {
    if (challengeId) {
      setChallengeId(challengeId);
      getEntityData(challengeId);
    }

    return setDefaultValues;
  }, [challengeId]);

  if (!challengeId) {
    throw new Error('Challenge ID is not defined');
  }

  const handleVerifyCode = async (consumer: string, code: string) => {
    try {
      const { challengeParticipantId, scanIqCustomer } =
        await twoFAService.verifyCodeForChallenge(
          {
            to: consumer,
            code,
            targetId: challengeId,
          },
          {
            ignoreErrorCodes: [
              RESPONSE_STATUSES.VERIFICATION_CODE_INCORRECT,
              RESPONSE_STATUSES.NUTRITION_ACCOUNT_NOT_FOUND_FOR_CHALLENGE,
              RESPONSE_STATUSES.CHALLENGE_PARTICIPANT_ALREADY_EXIST,
            ],
          },
        );

      setParticipantId(challengeParticipantId);
      setCustomer(scanIqCustomer);

      goToNextStep();
    } catch (error: any) {
      switch (error.response?.status) {
        case RESPONSE_STATUSES.VERIFICATION_CODE_INCORRECT:
          throw error;
        case RESPONSE_STATUSES.CHALLENGE_PARTICIPANT_ALREADY_EXIST:
          goTo('/already-participant');
          break;
        case RESPONSE_STATUSES.NUTRITION_ACCOUNT_NOT_FOUND_FOR_CHALLENGE:
          {
            goTo('/nutrition-account-not-found', {
              params: { [pathQueryParams.challengeId]: challengeId },
            });
          }
          break;
      }
    }
  };

  const stepsComponents = [
    <TwoFAStep key="two-fa-step" onVerifyCode={handleVerifyCode} />,
    <SignUpInfoStep key="sign-up-info-step" />,
  ];

  return (
    <>
      <ChallengesHeader />
      <UnavailableWhenLoggedIn>
        <Container maxWidth="sm">
          <BlackBgRow bgRowHeight="340px">{stepsComponents[activeStep]}</BlackBgRow>
        </Container>
      </UnavailableWhenLoggedIn>
    </>
  );
});

angularizeWithProviders(NgzChallengeSignUp, 'ngzChallengeSignUp', {});
